import { AuditOutlined, PlusCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Alert, Card, Col, Row, Select, Space, theme } from 'antd';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLaunchOnly } from '../ais/useAisCapabilities';
import { useAisIdentity } from '../ais/useAisIdentity';
import { DashboardEntry } from '../components/DashboardEntry';
import { HelpSupport } from '../components/HelpSupport.tsx';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore.ts';
import type { UpdateEvent } from '../socket/types.ts';
import { useSubscription } from '../socket/useSubscription.ts';
import { DASHBOARD_DATA_QUERY } from './Dashboard.gql';
import { DesktopVersionInfo } from './dashboard/DesktopVersionInfo';
import { OrderReport } from './dashboard/OrderReport.tsx';
import { NewsList } from './shared/news/NewsList.tsx';

const { useToken } = theme;

// fallow-ignore-next-line complexity
export const Dashboard: React.FC = () => {
  const { token } = useToken();
  const navigate = useNavigate();
  const { currentDoctorId } = useCurrentContextStore();
  const launchOnly = useLaunchOnly();
  // an AIS session runs inside the AIS, so the desktop client has nothing to offer there
  const aisSession = useAisIdentity() !== null;
  const tileSpan = launchOnly ? 12 : 8;
  const panelSpan = aisSession ? 12 : 8;
  const [range, setRange] = useState<'today' | 'week' | 'month' | 'year'>('today');

  const { data, loading, refetch } = useQuery(DASHBOARD_DATA_QUERY, {
    variables: {
      doctorId: currentDoctorId,
    },
  });

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  useSubscription({ type: 'request-created', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-sent', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-deleted', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'news-updated' }, callback);

  let sentRequests = 0;
  switch (range) {
    case 'today':
      sentRequests = data?.dashboardData?.sentRequestsToday ?? 0;
      break;
    case 'week':
      sentRequests = data?.dashboardData?.sentRequestsWeek ?? 0;
      break;
    case 'month':
      sentRequests = data?.dashboardData?.sentRequestsMonth ?? 0;
      break;
    case 'year':
      sentRequests = data?.dashboardData?.sentRequestsYear ?? 0;
      break;
  }

  const showOpenRequestsWarning = (data?.dashboardData?.openRequests ?? 0) > 300;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%', padding: token.paddingLG }}>
      {showOpenRequestsWarning ? (
        <Alert
          showIcon
          type="warning"
          title="Bitte offene Anforderungen senden oder aufräumen!"
          description="Es existieren mehr als 300 offene Anforderungen. Bitte senden Sie die relevanten Anforderungen, oder löschen Sie die nicht mehr benötigten offenen Anforderungen."
        />
      ) : null}

      {!!data?.relevantNews?.length && <NewsList newsFragments={data.relevantNews} mode="dashboard" />}

      <Row gutter={[16, 16]}>
        {!launchOnly && (
          <Col xs={24} lg={tileSpan}>
            <DashboardEntry
              onClick={() => navigate('/anforderung')}
              title="Neue Anforderung erstellen"
              icon={PlusCircleOutlined}
              value=""
              loading={false}
              color={token.colorPrimary}
            />
          </Col>
        )}
        <Col xs={24} lg={tileSpan}>
          <DashboardEntry
            onClick={() => navigate('/offene-anforderungen')}
            title="Offene Anforderungen"
            icon={AuditOutlined}
            value={data?.dashboardData?.openRequests || 0}
            loading={loading}
            color={token.colorWarning}
          />
        </Col>
        <Col xs={24} lg={tileSpan}>
          <DashboardEntry
            onClick={() => navigate('/gesendete-anforderungen')}
            title="Gesendete Anforderungen"
            icon={SendOutlined}
            value={sentRequests}
            loading={loading}
            color={token.colorSuccess}
            extra={
              // Wrap the Select so option clicks don't bubble up to the Card's
              // navigate onClick. The dropdown popup is portaled but its clicks
              // still bubble through the React tree, and Select's own onClick
              // only covers the box, not the popup — so the wrapper must.
              <span onClick={e => e.stopPropagation()}>
                <Select size="small" value={range} style={{ width: '130px' }} onChange={value => setRange(value)}>
                  <Select.Option value="today">Heute</Select.Option>
                  <Select.Option value="week">Diese Woche</Select.Option>
                  <Select.Option value="month">Dieses Monat</Select.Option>
                  <Select.Option value="year">Dieses Jahr</Select.Option>
                </Select>
              </span>
            }
          />
        </Col>
        <Col xs={24} lg={12} xxl={panelSpan}>
          <Card title="Auftragsbericht" style={{ height: '100%' }}>
            <OrderReport doctorId={currentDoctorId} />
          </Card>
        </Col>
        {!aisSession && (
          <Col xs={24} lg={12} xxl={8}>
            <Card title="Medcom Desktop" style={{ height: '100%' }}>
              <DesktopVersionInfo />
            </Card>
          </Col>
        )}
        <Col xs={24} lg={12} xxl={panelSpan}>
          <Card title="Hilfe & Support" style={{ height: '100%' }}>
            <HelpSupport />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};
